import React, { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { AuthSigningInScreen } from './AuthCard';
import { ROLES } from './auth';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRoles?: string[];
  requireAllRoles?: boolean;
  fallbackPath?: string;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRoles = [],
  requireAllRoles = false,
  fallbackPath = '/login',
}) => {
  const { isAuthenticated, isLoading, isInitialized, hasRole, hasAnyRole } = useAuth();
  const location = useLocation();

  if (!isInitialized || isLoading) {
    return <AuthSigningInScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to={fallbackPath} state={{ from: location }} replace />;
  }

  if (requiredRoles.length > 0) {
    const hasRequired = requireAllRoles
      ? requiredRoles.every((role) => hasRole(role))
      : hasAnyRole(requiredRoles);
    if (!hasRequired) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  return <>{children}</>;
};

export const AdminRoute: React.FC<{ children: ReactNode }> = ({ children }) => (
  <ProtectedRoute requiredRoles={['admin']}>{children}</ProtectedRoute>
);

export const CalibrationsRoute: React.FC<{ children: ReactNode }> = ({ children }) => (
  <ProtectedRoute requiredRoles={[...ROLES]}>{children}</ProtectedRoute>
);

export default ProtectedRoute;
